// eslint-disable-next-line no-restricted-imports
import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import { type EditorActions, WithEditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { CardClient, SmartCardProvider } from '@atlaskit/link-provider';
import {
	GenericErrorPage,
	HubErrorBoundary,
	useExtensionsActions,
	useExtensionsState,
} from '@atlassian/company-hub-common';
import {
	HubEditorConfigPanelEntryPoint,
	useHubEditorExtensionConfigPanel,
} from '@atlassian/hub-editor-config-panel';
import { type AkEditorLinkingProp, EditorLinkingFeatures } from '@atlassian/hub-editor-features';
import { getEmojiProvider, useHubExtensionProviders } from '@atlassian/hub-providers';

import { LINKING_FEATURES_CARD_OPTIONS } from '../../constants';
import { createHubEditorPreset } from '../../controllers/use-hub-editor-preset';
import type { HubEditorProps } from '../../types';

interface EditorInternalProps extends HubEditorProps {
	editorActions: EditorActions;
}

const EditorInternal = (props: EditorInternalProps) => {
	const intl = useIntl();
	const { editorActions, contentId, userId, cloudId, product } = props;
	const smartCardClient = useMemo(() => new CardClient(), []);

	const { closeConfigPanel, openConfigPanel } = useHubEditorExtensionConfigPanel(editorActions);
	const [{ selectedExtensionLocalId }] = useExtensionsState();
	const { setIsInViewMode } = useExtensionsActions();
	const hubExtensionProviders = useHubExtensionProviders({
		contentId,
		intl,
		PropsProvider: React.useMemo(() => (props) => <div>{props.children}</div>, []), // todo: Wireup with the product dependencies
		editorActions,
		openConfigPanel,
	});

	const { preset, editorApi } = usePreset(
		() =>
			createHubEditorPreset({
				intl,
				...props,
			}),
		[],
	);

	useEffect(() => {
		setIsInViewMode(false);
	}, [setIsInViewMode]);

	return (
		<SmartCardProvider product={product} client={smartCardClient}>
			<EditorLinkingFeatures
				product={product}
				cloudId={cloudId}
				options={LINKING_FEATURES_CARD_OPTIONS}
			>
				{(linkingFeatures: AkEditorLinkingProp) => {
					return (
						<ComposableEditor
							appearance="full-page"
							{...linkingFeatures}
							{...props}
							preset={preset}
							{...(hubExtensionProviders.length > 0
								? { extensionProviders: hubExtensionProviders }
								: {})}
							editorActions={editorActions}
							contextPanel={
								<HubEditorConfigPanelEntryPoint
									localId={selectedExtensionLocalId || ''}
									closeConfigPanel={closeConfigPanel}
									editorActions={editorActions}
									contentId={contentId}
									editorApi={editorApi}
								/>
							}
							emojiProvider={getEmojiProvider({
								cloudId,
								userId,
							})}
						/>
					);
				}}
			</EditorLinkingFeatures>
		</SmartCardProvider>
	);
};

const HubEditor = (props: HubEditorProps) => {
	return (
		<HubErrorBoundary
			packageName="@atlassian/hub-editor"
			id="hub-editor"
			fallback={<GenericErrorPage />}
		>
			<EditorContext>
				<WithEditorActions
					render={(editorActions: EditorActions) => (
						<EditorInternal editorActions={editorActions} {...props} />
					)}
				/>
			</EditorContext>
		</HubErrorBoundary>
	);
};

export default HubEditor;
